"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScoCardEodView = exports.ScoCardEodCommandView = exports.IssueScoCardEodCommand = exports.ScoCardEodReportResponse = exports.ScoCardEodCommand = exports.ScoCardEodReport = exports.ScoCardEodCommandResult = exports.ScoCardEodDutyReport = exports.ScoCardEodCommandOutcome = exports.ScoCardEodLaneOutcome = exports.ScoCardEodDecision = exports.ScoCardEodDutyState = exports.SCO_CARD_EOD_REPORT_FRESH_MS = exports.SCO_CARD_EOD_COMMAND_TTL_MS = exports.SCO_CARD_EOD_MAX_RESULTS = exports.SCO_CARD_EOD_MAX_DUTIES = void 0;
exports.scoCardEodIsSimulator = scoCardEodIsSimulator;
const zod_1 = require("zod");
/**
 * MICRO-46 — Back Office decides an uncertain card end of day on a micromarket
 * self-checkout lane.
 *
 * After each Z the lane owes its card terminal an EndOfDay. When one may have
 * reached the terminal but its result is unknown, the duty is `uncertain` and
 * automatic end of day for that terminal pauses until a person decides. The
 * lane's own service menu can decide it (terminals MICRO-46); this channel
 * lets Back Office decide it remotely, for unattended stores.
 *
 * Owner decisions (2026-10-22/23):
 * - `settled` only after a check against the bank / EPS batch report; from
 *   Back Office the report's date and number are typed and stored;
 * - a separate permission (`sco-card-eod.decide`), owner/admin only; one
 *   person, audited, no second approver;
 * - simulator duties (card terminal `SIM-*`) may be discarded without the check;
 * - a command the lane does not collect within 24 h expires;
 * - the lane reports its open duties on every sync.
 *
 * The channel copies the Link's retry-request handoff: the lane reports (open
 * duties, results of earlier commands) on `POST /sync/sco/card-eod`, and the
 * answer carries the commands addressed to that lane. A command is handed out
 * on every report until its result arrives; the lane applies a `commandId`
 * once and answers a repeat with the first outcome.
 *
 * Nothing here carries an amount, a card number or free text: errors and
 * reasons are stable codes.
 */
/** Most open duties one report may carry, and most commands one answer carries. */
exports.SCO_CARD_EOD_MAX_DUTIES = 50;
exports.SCO_CARD_EOD_MAX_RESULTS = 50;
/** How long a command waits to be collected before it expires (owner, 24 h). */
exports.SCO_CARD_EOD_COMMAND_TTL_MS = 24 * 60 * 60 * 1000;
/** How recent a lane's report must be for Back Office to decide on it. */
exports.SCO_CARD_EOD_REPORT_FRESH_MS = 15 * 60 * 1000;
/** A stable code (`dispatch_unknown`, `eps_timeout`); never a message. */
const Code = zod_1.z
    .string()
    .max(64)
    .regex(/^[a-z][a-z0-9_.-]*$/, 'a stable code, not free text');
/** The lane's card terminal (`eps:<id>@<host>:<port>`), as its journal names it. */
const CardTerminal = zod_1.z.string().min(1).max(128);
/** The lane's duty states that are still open (`done` never is). */
exports.ScoCardEodDutyState = zod_1.z.enum([
    'preparing',
    'due',
    'attempting',
    'unsent',
    'uncertain',
]);
exports.ScoCardEodDecision = zod_1.z.enum(['settled', 'not_settled', 'simulator_discarded']);
/** What the lane did with a command (its `ScoCardEndOfDayStaffOutcome`). */
exports.ScoCardEodLaneOutcome = zod_1.z.enum([
    'applied',
    'not_found',
    'not_uncertain',
    'check_not_confirmed',
    'not_simulator',
    'not_current_terminal',
    'no_actor',
    'not_recorded',
]);
/** A command's outcome as Back Office records it: the lane's, or `expired`. */
exports.ScoCardEodCommandOutcome = zod_1.z.enum([...exports.ScoCardEodLaneOutcome.options, 'expired']);
/** One open duty as the lane reports it. */
exports.ScoCardEodDutyReport = zod_1.z.object({
    dutyId: zod_1.z.string().min(1).max(64),
    /** Null when the lane does not know which terminal owes it. */
    cardTerminal: CardTerminal.nullable(),
    fiscalDay: zod_1.z.number().int().min(0).nullable(),
    closedAt: zod_1.z.string().datetime().nullable(),
    state: exports.ScoCardEodDutyState,
    reason: Code.nullable(),
    since: zod_1.z.string().datetime(),
    attempts: zod_1.z.number().int().min(0),
    lastErrorKey: Code.nullable(),
});
exports.ScoCardEodCommandResult = zod_1.z.object({
    commandId: zod_1.z.string().uuid(),
    outcome: exports.ScoCardEodLaneOutcome,
    at: zod_1.z.string().datetime(),
});
/** `POST /sync/sco/card-eod`: the lane's open duties and command results. */
exports.ScoCardEodReport = zod_1.z.object({
    /** The card terminal an EndOfDay would reach now; null when none is set up. */
    currentCardTerminal: CardTerminal.nullable(),
    duties: zod_1.z.array(exports.ScoCardEodDutyReport).max(exports.SCO_CARD_EOD_MAX_DUTIES),
    results: zod_1.z.array(exports.ScoCardEodCommandResult).max(exports.SCO_CARD_EOD_MAX_RESULTS),
});
/** One command, as the lane receives it. */
exports.ScoCardEodCommand = zod_1.z.object({
    commandId: zod_1.z.string().uuid(),
    dutyId: zod_1.z.string().min(1).max(64),
    /** The lane refuses (`not_found`) when the duty names another terminal or day. */
    expectedCardTerminal: CardTerminal.nullable(),
    fiscalDay: zod_1.z.number().int().min(0).nullable(),
    decision: exports.ScoCardEodDecision,
    bankReportChecked: zod_1.z.boolean(),
    /** The bank / EPS batch report checked; set for `settled`, null otherwise. */
    bankReport: zod_1.z
        .object({ date: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/), number: zod_1.z.string().min(1).max(64) })
        .nullable(),
    actor: zod_1.z.object({ userId: zod_1.z.string(), name: zod_1.z.string().min(1).max(128) }),
    issuedAt: zod_1.z.string().datetime(),
    expiresAt: zod_1.z.string().datetime(),
});
exports.ScoCardEodReportResponse = zod_1.z.object({
    commands: zod_1.z.array(exports.ScoCardEodCommand).max(exports.SCO_CARD_EOD_MAX_RESULTS),
});
/** `POST /sco/card-eod/commands`: an operator's decision on one duty. */
exports.IssueScoCardEodCommand = zod_1.z
    .object({
    terminalId: zod_1.z.string().min(1),
    dutyId: zod_1.z.string().min(1).max(64),
    decision: exports.ScoCardEodDecision,
    bankReportChecked: zod_1.z.boolean().default(false),
    bankReportDate: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'the bank report date as YYYY-MM-DD')
        .optional(),
    bankReportNumber: zod_1.z.string().trim().min(1).max(64).optional(),
})
    .superRefine((value, ctx) => {
    if (value.decision !== 'settled')
        return;
    if (!value.bankReportChecked) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['bankReportChecked'],
            message: 'settled requires the bank / EPS batch report to have been checked',
        });
    }
    for (const field of ['bankReportDate', 'bankReportNumber']) {
        if (value[field] === undefined) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [field],
                message: 'settled requires the bank batch report date and number',
            });
        }
    }
});
/** A command as an operator sees it. */
exports.ScoCardEodCommandView = exports.ScoCardEodCommand.extend({
    deliveredAt: zod_1.z.string().datetime().nullable(),
    completedAt: zod_1.z.string().datetime().nullable(),
    outcome: exports.ScoCardEodCommandOutcome.nullable(),
});
/** `GET /sco/card-eod?terminalId=`: a lane's open duties and their commands. */
exports.ScoCardEodView = zod_1.z.object({
    terminalId: zod_1.z.string(),
    /** The lane's last report; null when it never reported. */
    reportedAt: zod_1.z.string().datetime().nullable(),
    /** Whether the report is recent enough to decide on. */
    fresh: zod_1.z.boolean(),
    currentCardTerminal: CardTerminal.nullable(),
    duties: zod_1.z.array(exports.ScoCardEodDutyReport.extend({
        simulator: zod_1.z.boolean(),
        commands: zod_1.z.array(exports.ScoCardEodCommandView),
    })),
});
/**
 * Whether a card terminal is a simulator: its id, after an optional `eps:`
 * and before an optional `@host`, starts with `SIM-` (the lane's
 * `scoCardEndOfDayIsSimulator`). Unknown is not a simulator.
 */
function scoCardEodIsSimulator(cardTerminal) {
    if (cardTerminal === null)
        return false;
    let id = cardTerminal.trim();
    if (id.startsWith('eps:'))
        id = id.substring(4);
    const at = id.indexOf('@');
    if (at >= 0)
        id = id.substring(0, at);
    return id.startsWith('SIM-') && id.length > 4;
}
//# sourceMappingURL=sco-card-eod.js.map